import angular from 'angular'
import invokeMap from 'lodash/invokeMap'
import mapValues from 'lodash/mapValues'

import { $q } from '../ng-compat/imports'
import type { ProductSkuIndex } from '../factories/search/product-sku-index'

/**
 * Manages the various search indexes and sources.
 *
 * @module webregister.managers.searchManager
 * @class SearchManager
 * @constructor
 */
export class SearchManager {
  private _exactMatchSources: $TSFixMe
  private _sources: $TSFixMe
  private _whenInitialised: $TSFixMe

  constructor(
    productSkuIndex: ProductSkuIndex,
    customerCodeIndex: $TSFixMe,
    typeySearchSource: $TSFixMe
  ) {
    this._sources = {
      all: [productSkuIndex, customerCodeIndex, typeySearchSource],
      product: [productSkuIndex, typeySearchSource],
      customer: [customerCodeIndex, typeySearchSource],
    }
    this._exactMatchSources = {
      product: productSkuIndex,
      customer: customerCodeIndex,
    }
  }

  // ============================== Public API ============================== //

  /**
   * Initialise the search manager.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once the search manager has initialised
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = this._init()
    }

    return this._whenInitialised
  }

  /**
   * Determine when the search sources have completed syncing.
   *
   * @method whenSyncComplete
   *
   * @param  {String} [type = 'all']
   *         Optionally filter which type of search sources you care about ('product', 'customer'). Defaults to all.
   *
   * @return {Promise} A promise resolved once the search sources have completed syncing
   */
  public whenSyncComplete(type: string = 'all') {
    return this.init().then(() =>
      $q.all(invokeMap(this._sources[type], 'whenSyncComplete'))
    )
  }

  /**
   * Get the array of search sources that can be passed to an AutocompleteManager.
   *
   * @method getSources
   *
   * @param  {String} [type = 'all']
   *         Optionally filter which type of search sources you care about ('product', 'customer'). Defaults to all.
   *
   * @return {Array<AutocompleteSource>} Array of search sources
   */
  public getSources(type: string = 'all') {
    return this._sources[type]
  }

  /**
   * Find results that match the given query exactly optionally filtered to a specific type of result. For example find
   * products with a SKU that exactly matches the query.
   *
   * @method findExactMatch
   *
   * @param  {Object} options
   *
   *         @param  {String} options.query
   *                 The query to search for exact matches
   *
   *         @param  {String} [options.type]
   *                 Optional type to search for e.g. 'product', 'customer'
   *
   * @return {Promise} A promise that resolves to the exact matches found. If a type is specified this will be the
   *         array of results. If no type is specified this will be an object `{ product: [...], customer: [...] }`.
   */
  public findExactMatch({ query, type }: { query: string; type?: string }) {
    if (type) {
      return this._exactMatchSources[type].search(query)
    } else {
      return $q.all(
        mapValues(this._exactMatchSources, source => source.search(query))
      )
    }
  }

  // ============================== Private API ============================== //

  /**
   * Initialise the search manager, determining what search sources to use and initialising them.
   *
   * @private
   * @method _init
   *
   * @return {Promise} A promise resolved once the search manager and its sources have initialised
   */
  private _init() {
    return $q.all(invokeMap(this._sources.all, 'init'))
  }
}

angular.module('webregister.managers').service('searchManager', SearchManager)
